import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import apiUrls from "../../../../../api";

interface HotspotZoneDetect {
  id_internal: number;
  is_selected: boolean;
  status: number; // 1 = found, 0 = not found
}

interface HotspotInfo {
  idInternal: number;
  explanation?: string;
  entitled?: string;
  description?: string;
  is_true_response?: boolean;
  [key: string]: unknown;
}

interface ZoneDetectDetailProps {
  data: {
    list_hotspot_zone_detect?: HotspotZoneDetect[];
  };
  contentId?: number;
}

const ZoneDetectDetail = ({ data, contentId }: ZoneDetectDetailProps) => {
  const { t } = useTranslation();
  const [hotspotInfos, setHotspotInfos] = useState<HotspotInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const fetchedContentIdRef = useRef<number | null>(null);

  const zones = data.list_hotspot_zone_detect || [];

  // Fetch content-zone-detect to get hotspot_info (zone names)
  useEffect(() => {
    const fetchContentData = async () => {
      if (!contentId) return;

      // Prevent duplicate fetches
      if (fetchedContentIdRef.current === contentId) return;
      fetchedContentIdRef.current = contentId;

      setIsLoading(true);

      try {
        // Fetch content-zone-detect by filtering on content.id
        const zoneDetectResponse = await axios.get(
          `${apiUrls.base}/api/content-zone-detects?filters[content][id][$eq]=${contentId}`,
        );

        const results = zoneDetectResponse.data?.data || zoneDetectResponse.data;
        const zoneDetectData = Array.isArray(results) ? results[0] : results;

        if (zoneDetectData) {
          const hotspotInfo =
            zoneDetectData?.hotspot_info || zoneDetectData?.attributes?.hotspot_info;

          if (Array.isArray(hotspotInfo)) {
            setHotspotInfos(hotspotInfo);
          } else if (hotspotInfo && typeof hotspotInfo === "object") {
            // Sometimes it's stored as an object with numeric keys
            const infosArray = Object.values(hotspotInfo) as HotspotInfo[];
            setHotspotInfos(infosArray);
          }
        }
      } catch (err) {
        console.error("Error fetching zone detect content:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchContentData();
  }, [contentId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" />
      </div>
    );
  }

  if (hotspotInfos.length === 0 && !isLoading) {
    return <div className="text-center py-8 text-gray-500">{t("statsNew.interaction.noData")}</div>;
  }

  // Get hotspot info by idInternal
  const getHotspotInfo = (idInternal: number): HotspotInfo | undefined => {
    return hotspotInfos.find((h) => h.idInternal === idInternal);
  };

  // Filter zones into categories
  const selectedZones = zones.filter((z) => z.is_selected);
  const missedZones = hotspotInfos.filter((h) => {
    const isTrueResponse = h.is_true_response === true;
    const wasSelected = zones.some((z) => z.id_internal === h.idInternal && z.is_selected);
    return isTrueResponse && !wasSelected;
  });

  const hasNoResults = selectedZones.length === 0 && missedZones.length === 0;

  if (hasNoResults) {
    return <div className="text-center py-8 text-gray-500">{t("statsNew.interaction.noData")}</div>;
  }

  return (
    <div className="h-full flex flex-col overflow-y-auto">
      {/* Zones list with teal border */}
      <div className="border-2 border-teal-200 rounded-lg p-4 mb-4">
        <h4 className="text-sm font-semibold text-gray-700 mb-4">
          {t("statsNew.interaction.zoneDetect.zones")}
        </h4>

        <div className="space-y-2">
          {/* User's selections */}
          {selectedZones.map((zone, index) => {
            const hotspot = getHotspotInfo(zone.id_internal);
            const zoneName =
              hotspot?.explanation ||
              hotspot?.entitled ||
              hotspot?.description ||
              `Zone ${zone.id_internal}`;
            const isCorrect = hotspot?.is_true_response === true;

            return (
              <div
                key={`selected-${index}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg ${
                  isCorrect ? "bg-green-50" : "bg-red-50"
                }`}
              >
                {/* Status icon with tooltip */}
                <div
                  className="flex-shrink-0"
                  title={
                    isCorrect
                      ? t("statsNew.interaction.zoneDetect.tooltipCorrect")
                      : t("statsNew.interaction.zoneDetect.tooltipError")
                  }
                >
                  {isCorrect ? (
                    <svg
                      className="h-5 w-5 text-green-600 cursor-help"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  ) : (
                    <svg
                      className="h-5 w-5 text-red-600 cursor-help"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  )}
                </div>

                {/* Zone name */}
                <div className="flex-1">
                  <p className="text-sm text-gray-900">{zoneName}</p>
                </div>

                {/* Zone number */}
                <div className="flex-shrink-0 text-sm text-gray-500 font-medium">
                  #{zone.id_internal}
                </div>
              </div>
            );
          })}

          {/* Missed zones (good zones not selected) */}
          {missedZones.map((hotspot, index) => {
            const zoneName =
              hotspot.explanation ||
              hotspot.entitled ||
              hotspot.description ||
              `Zone ${hotspot.idInternal}`;

            return (
              <div
                key={`missed-${index}`}
                className="flex items-center gap-3 px-4 py-3 rounded-lg bg-yellow-50"
              >
                {/* Warning icon with tooltip */}
                <div
                  className="flex-shrink-0"
                  title={t("statsNew.interaction.zoneDetect.tooltipMissed")}
                >
                  <svg
                    className="h-5 w-5 text-yellow-500 cursor-help"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                </div>

                {/* Zone name */}
                <div className="flex-1">
                  <p className="text-sm text-gray-900">{zoneName}</p>
                </div>

                {/* Zone number */}
                <div className="flex-shrink-0 text-sm text-gray-500 font-medium">
                  #{hotspot.idInternal}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-xs">
        <div className="flex items-center gap-1.5">
          <svg
            className="h-4 w-4 text-green-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <span className="text-gray-600">
            {t("statsNew.interaction.zoneDetect.tooltipCorrect")}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <svg
            className="h-4 w-4 text-red-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
          <span className="text-gray-600">{t("statsNew.interaction.zoneDetect.tooltipError")}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <svg
            className="h-4 w-4 text-yellow-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <span className="text-gray-600">
            {t("statsNew.interaction.zoneDetect.tooltipMissed")}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ZoneDetectDetail;
